/* eslint-disable import/order */
import { Box, useDisclosure } from '@chakra-ui/react';
import '@fullcalendar/react/dist/vdom';
import FullCalendar, { DatesSetArg, EventDropArg } from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg, EventDragStartArg, EventDragStopArg } from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import adaptivePlugin from '@fullcalendar/adaptive';
import clsx from 'clsx';
import { format, add } from 'date-fns';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ControllableDrawer } from 'components/Drawer';
import ScheduleDetail from 'containers/Scheduling/RightDrawer/ScheduleDetail';
import ScheduleForm from 'containers/Scheduling/RightDrawer/ScheduleForm';
import { initialScheduleFormData } from 'containers/Scheduling/RightDrawer/ScheduleForm/schema';
import FilterGroup from 'containers/Scheduling/shared/FilterGroup';
import { roles } from 'global/rolesAndPermissions';
import { useFetchCalendarView, ICalendarViewSearchQuery } from 'hooks/scheduling/useFetchCalendarView';
import { useUpdateScheduleShift } from 'hooks/scheduling/useUpdateScheduleShift';

import EventContent from './EventContent';
import ResourceLabelContent from './ResourceLabelContent';
import './Schedule.scss';
import DuplicateSchedulePopulator from 'containers/Scheduling/RightDrawer/DuplicateSchedulePopulator';
import { useUserObject } from 'store/useUserStore';

const Schedule: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'scheduling']);

  const userStore = useUserObject();
  const firstDay = useMemo(() => userStore?.companyInfo?.start_week ?? 0, [userStore]);
  const fullCalendarRef = useRef<any>(null);

  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [scheduleFormData, setScheduleFormData] = useState<typeof initialScheduleFormData>(initialScheduleFormData);
  const [calendarData, setCalendarData] = useState<{
    dayGridEvents: any[];
    timelineEvents: any[];
    resources: any[];
  }>({ dayGridEvents: [], timelineEvents: [], resources: [] });
  const [events, setEvents] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<ICalendarViewSearchQuery>({
    schedule_status: 'all',
    search_schedule_by_employee_id: '',
    search_schedule_by_job_id: '',
    search_schedule_by_date_range: '',
  });

  const {
    isOpen: isScheduleFormDrawerOpen,
    onOpen: onScheduleFormDrawerOpen,
    onClose: onScheduleFormDrawerClose,
  } = useDisclosure();
  const {
    isOpen: isScheduleDetailDrawerOpen,
    onOpen: onScheduleDetailDrawerOpen,
    onClose: onScheduleDetailDrawerClose,
  } = useDisclosure();
  const {
    isOpen: isDuplicateScheduleDrawerOpen,
    onOpen: onDuplicateScheduleDrawerOpen,
    onClose: onDuplicateScheduleDrawerClose,
  } = useDisclosure();

  const { data, isFetching } = useFetchCalendarView(searchQuery, {
    enabled: searchQuery.search_schedule_by_date_range ? true : false,
  });
  const { mutateAsync: mutateAsyncUpdateScheduleShift, isLoading } = useUpdateScheduleShift();

  useEffect(() => {
    if (data?.payload) {
      const dayGridEvents: any[] = [];

      const timelineEvents = Array.isArray(data?.payload?.events)
        ? data?.payload?.events.map((item: any) => {
            const dayGridEvent = {
              ...item,
              startDate: item?.timezone_dateinfo ? item?.timezone_dateinfo?.start_date : item.start,
              endDate: item?.timezone_dateinfo ? item?.timezone_dateinfo?.end_date : item.end,
              start: item?.timezone_dateinfo ? item?.timezone_dateinfo?.start_date : item.start,
              end: item?.timezone_dateinfo ? item?.timezone_dateinfo?.end_date : item.end,
              resourceId: item?.userId[0],
              scheduleId: item.id,
              display: 'block',
              textColor: item.color === '#FFFF00' ? 'var(--timeero-colors-primary-900)' : 'var(--timeero-colors-white)',
            };

            dayGridEvents.push(dayGridEvent);

            return item?.userId.map((user: any) => ({
              ...dayGridEvent,
              resourceId: user,
              id: `${item.id}-${user}`,
            }));
          })
        : [];

      const fetchedResources = Array.isArray(data?.payload?.users)
        ? data?.payload?.users.map((item: any) => {
            return {
              ...item,
              user: item?.firstName + ' ' + item?.lastName,
              id: item?.userId,
            };
          })
        : [];

      if (fullCalendarRef.current) {
        if (fullCalendarRef.current.getApi().view.type === 'dayGridMonth') {
          setEvents(dayGridEvents);
        } else {
          setEvents(timelineEvents.flat());
        }
      } else {
        setEvents(dayGridEvents);
      }

      setCalendarData({
        dayGridEvents: dayGridEvents,
        timelineEvents: timelineEvents.flat(),
        resources: fetchedResources,
      });
    }
  }, [data]);

  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    const dateRange = format(arg.start, 'yyyy-MM-dd') + ',' + format(arg.end, 'yyyy-MM-dd');
    setSearchQuery((searchQueryParam: any) => ({ ...searchQueryParam, search_schedule_by_date_range: dateRange }));
  }, []);

  const handleDateClick = useCallback((arg: DateClickArg) => {
    if (userStore?.userInfo?.roleId !== roles.ADMIN && userStore?.permissions?.manageSchedules === false) return;

    const formData = {
      ...initialScheduleFormData,
      start_date: arg.date,
      end_date: arg.date,
      start_time: format(arg.date, 'HH:mm:ss'),
      end_time: format(arg.date, 'HH:mm:ss'),
    };

    setScheduleFormData(formData);
    onScheduleFormDrawerOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEventClick = useCallback((arg: any) => {
    setSelectedScheduleId(arg.event._def.extendedProps.scheduleId);
    onScheduleDetailDrawerOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEventDragStart = useCallback((arg: EventDragStartArg) => {
    if (arg.jsEvent.altKey) {
      // Duplicating current event object for simulating duplicate behaviour
      const currentEventObj = arg.event.toPlainObject();
      currentEventObj.id = 'DUPLICATE';

      const resources = arg.event.getResources();
      if (resources.length) {
        currentEventObj.resourceId = resources[0]._resource.id;
      }

      arg.view.calendar.addEvent(currentEventObj);
    }
  }, []);

  const handleEventDragStop = useCallback((arg: EventDragStopArg) => {
    //Removing duplicated event
    const duplicateEvent = arg.view.calendar?.getEventById('DUPLICATE');
    duplicateEvent?.remove();
  }, []);

  const handleEventDrop = useCallback((arg: EventDropArg) => {
    if (arg.jsEvent.altKey) {
      // get current event resources for adding to the user list.
      const user_ids = arg.event
        .getResources()
        .filter(resource => {
          const existingUserIds = arg.event._def.extendedProps.userId;
          return existingUserIds.includes(Number(resource._resource.id)) ? false : true;
        })
        .map(resource => resource._resource.extendedProps);

      const startDate = arg.event.start;
      const endDate = arg.event.end || arg.event.start;

      const formData = {
        ...initialScheduleFormData,
        start_date: startDate,
        end_date: endDate,
        start_time: startDate ? format(startDate, 'HH:mm:ss') : format(new Date(), 'HH:mm:ss'),
        end_time: endDate ? format(endDate, 'HH:mm:ss') : format(new Date(), 'HH:mm:ss'),
        user_ids: user_ids,
      };

      // Update current event date to previous one
      // NOTE: This Code should stay at last as it will change the event object date and resources
      const negativeDelta = Object.fromEntries(Object.entries(arg.delta).map(([key, value]) => [key, -value]));
      arg.event.moveDates(negativeDelta);
      arg.event.setResources(arg.oldEvent.getResources());

      setScheduleFormData(formData);
      setSelectedScheduleId(arg.event._def.extendedProps.scheduleId!);
      onDuplicateScheduleDrawerOpen();
    } else {
      const { days, milliseconds, months, years } = arg.delta;

      const newStartDate =
        arg.event.start ||
        add(new Date(arg.event._def.extendedProps.startDate), {
          years: years,
          months: months,
          days: days,
          seconds: milliseconds / 1000,
        });

      const newEndDate =
        arg.event.end ||
        add(new Date(arg.event._def.extendedProps.endDate), {
          years: years,
          months: months,
          days: days,
          seconds: milliseconds / 1000,
        });

      const postData = {
        schedule_id: arg.event._def.extendedProps.scheduleId,
        from_user_id: arg.oldResource?.id || '',
        to_user_id: arg.newResource?.id || '',
        event_pid: arg.event._def.extendedProps?.event_pid || '',
        start_date: format(newStartDate, 'MM/dd/yyyy'),
        end_date: format(newEndDate, 'MM/dd/yyyy'),
        start_time: format(newStartDate, 'hh:mm:ss a'),
        end_time: format(newEndDate, 'hh:mm:ss a'),
      };

      mutateAsyncUpdateScheduleShift(postData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = useCallback((selectedStatus: ILabelValueOptions) => {
    if (selectedStatus) {
      setSearchQuery((searchQueryParam: any) => ({ ...searchQueryParam, schedule_status: selectedStatus.value }));
    } else {
      setSearchQuery((searchQueryParam: any) => ({ ...searchQueryParam, schedule_status: 'all' }));
    }
  }, []);

  const handleEmployeeFilterFormSubmit = useCallback((selectedEmployees: any) => {
    const updatedEmployee = selectedEmployees.length ? selectedEmployees.map((item: any) => item.userId).join(',') : '';

    setSearchQuery((searchQueryParam: any) => ({
      ...searchQueryParam,
      search_schedule_by_employee_id: updatedEmployee,
    }));
  }, []);

  const handleJobChange = useCallback((selectedJob: any) => {
    setSearchQuery((searchQueryParam: any) => ({ ...searchQueryParam, search_schedule_by_job_id: selectedJob?.id }));
  }, []);

  return (
    <Box>
      <ControllableDrawer isOpen={isScheduleFormDrawerOpen} onClose={onScheduleFormDrawerClose}>
        <ScheduleForm onClose={onScheduleFormDrawerClose} formData={scheduleFormData} />
      </ControllableDrawer>

      <ControllableDrawer isOpen={isScheduleDetailDrawerOpen} onClose={onScheduleDetailDrawerClose}>
        <ScheduleDetail onClose={onScheduleDetailDrawerClose} scheduleId={selectedScheduleId} />
      </ControllableDrawer>

      <ControllableDrawer isOpen={isDuplicateScheduleDrawerOpen} onClose={onDuplicateScheduleDrawerClose}>
        <DuplicateSchedulePopulator
          onClose={onDuplicateScheduleDrawerClose}
          scheduleId={selectedScheduleId}
          formData={scheduleFormData}
        />
      </ControllableDrawer>

      <FilterGroup
        handleStatusChange={handleStatusChange}
        handleEmployeeFilterFormSubmit={handleEmployeeFilterFormSubmit}
        handleJobChange={handleJobChange}
      />
      <Box
        className={clsx(
          'schedule-box',
          'visible-print',
          isFetching && 'schedule-box--loading',
          isLoading && 'schedule-box--loading'
        )}
      >
        <FullCalendar
          ref={fullCalendarRef}
          schedulerLicenseKey={import.meta.env.VITE_FULL_CALENDAR_KEY}
          plugins={[resourceTimelinePlugin, dayGridPlugin, timeGridPlugin, interactionPlugin, adaptivePlugin]}
          initialView="dayGridMonth"
          aspectRatio={1.5}
          height={'80vh'}
          firstDay={firstDay}
          resourceAreaWidth={'250px'}
          resourceAreaColumns={[
            {
              field: 'user',
              headerContent: t('common:user'),
            },
          ]}
          displayEventEnd={true}
          editable={
            userStore?.userInfo?.roleId !== roles.ADMIN && userStore?.permissions?.manageSchedules === false
              ? false
              : true
          }
          eventDurationEditable={false}
          droppable={true}
          eventDragStop={handleEventDragStop}
          eventDragStart={handleEventDragStart}
          eventDrop={handleEventDrop}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          headerToolbar={{
            start: 'timelineDay,timelineWeek,gridMonth',
            center: 'title',
          }}
          views={{
            resourceTimelineWeek: {
              duration: { weeks: 1 },
              slotDuration: { days: 1 },
              slotLabelFormat: {
                weekday: 'short',
                day: '2-digit',
              },
            },
          }}
          customButtons={{
            gridMonth: {
              text: t('common:month'),
              click: () => {
                if (fullCalendarRef.current) {
                  setEvents(calendarData.dayGridEvents);
                  fullCalendarRef.current.getApi().changeView('dayGridMonth');
                }
              },
            },
            timelineWeek: {
              text: t('common:week'),
              click: () => {
                if (fullCalendarRef.current) {
                  setEvents(calendarData.timelineEvents);
                  fullCalendarRef.current.getApi().changeView('resourceTimelineWeek');
                }
              },
            },
            timelineDay: {
              text: t('common:day'),
              click: () => {
                if (fullCalendarRef.current) {
                  setEvents(calendarData.timelineEvents);
                  fullCalendarRef.current.getApi().changeView('resourceTimelineDay');
                }
              },
            },
          }}
          resourceOrder="user"
          handleWindowResize={true}
          // showNonCurrentDates={false}
          resources={calendarData.resources}
          events={events}
          resourceLabelContent={(arg: any) => <ResourceLabelContent arg={arg} />}
          eventContent={arg => <EventContent arg={arg} />}
        />
      </Box>
    </Box>
  );
};

export default Schedule;
